import { Info } from "@components";
import {
  BackendError,
  ForbiddenError,
  NetworkError,
  UnprocessableEntityError,
} from "@exceptions";
import {
  CircleCheck,
  FileIcon,
  Image as ImageIcon,
  TriangleAlert,
  Upload,
  X,
} from "@icons";
import { useAnnounce } from "@src/components/live-announcer";
import { NO_INTERPOLATION_ESCAPE } from "@src/raya-lucaria/i18n/interpolation";
import classNames from "classnames";
import {
  DragEvent,
  RefObject,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Label from "../_shared/label";
import { humanizeAccept } from "./humanize-accept";
import { matchesAccept } from "./matches-accept";

export type FileUploadStatus = "done" | "uploading" | "error";

export interface DisplayFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string | null;
  progress?: number;
  status: FileUploadStatus;
  error?: string | null;
  code?: string;
  isExisting?: boolean;
}

export interface UploadResult {
  code: string;
}

export type FileUploader = (
  file: File,
  onProgress: (percent: number) => void,
  signal: AbortSignal,
) => Promise<UploadResult>;

interface FileInputProps {
  name: string;
  label?: string;
  info?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string;
  value?: FileList | null;
  onChange?: (files: FileList | null) => void;
  inputRef?: RefObject<HTMLInputElement> | null;
  refCallback?: (ref: { focus: () => void }) => void;
  placeholder?: string;
  hint?: string;
  compact?: boolean;
  maxSize?: number;
  maxFiles?: number;
  existingFileUrl?: string | null;
  onDeleteExisting?: () => void;
  uploader?: FileUploader;
  onUploadedChange?: (codes: string[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

function formatSize(bytes: number): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(name: string): string {
  const match = (name || "").match(/\.([^.?]+)(\?.*)?$/);
  return match ? match[1].toUpperCase() : "FILE";
}

function isImageFile(name: string, type?: string): boolean {
  if (type && type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(name || "");
}

const PROGRESS_STEP_PERCENT = 25;

interface PendingFocus {
  kind: "trigger" | "replace" | "card";
  removedId?: string;
  index?: number;
  count: number;
}

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `f${idCounter}`;
}

export function FileInput({
  name,
  label = "",
  info,
  error = "",
  required = false,
  disabled = false,
  multiple = false,
  accept,
  value,
  onChange = () => {},
  inputRef = null,
  refCallback = () => {},
  placeholder,
  hint,
  compact = false,
  maxSize,
  maxFiles,
  existingFileUrl,
  onDeleteExisting = () => {},
  uploader,
  onUploadedChange = () => {},
  onUploadingChange = () => {},
}: FileInputProps) {
  const isUploadMode = typeof uploader === "function";

  const [displayInfo, setDisplayInfo] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [items, setItems] = useState<DisplayFile[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const controllers = useRef<Record<string, AbortController>>({});
  const milestones = useRef<Record<string, number>>({});
  const pendingFocus = useRef<PendingFocus | null>(null);
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const replaceRef = useRef<HTMLButtonElement>(null);
  const cardRemoveRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { t } = useTranslation();
  const announce = useAnnounce();

  const innerRef = inputRef || createRef<HTMLInputElement>();

  useEffect(() => {
    if (refCallback) {
      refCallback({
        focus: () => (triggerRef.current ?? replaceRef.current)?.focus(),
      });
    }
  }, [innerRef, refCallback]);

  useEffect(() => {
    return () => {
      Object.values(controllers.current).forEach((c) => c.abort());
    };
  }, []);

  useEffect(() => {
    return () => previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  useEffect(() => {
    if (isUploadMode) return;

    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    if (value && value.length > 0) {
      const urls: string[] = [];
      for (let i = 0; i < value.length; i++) {
        urls.push(URL.createObjectURL(value[i]));
      }
      setPreviewUrls(urls);
    } else {
      setPreviewUrls([]);
    }
  }, [value, isUploadMode]);

  const displayError = internalError || error;

  const validate = (files: File[]): string => {
    if (!multiple && files.length > 1) return t("fileErrorMaxOne");
    if (maxFiles && files.length > maxFiles) {
      return maxFiles > 1
        ? t("fileErrorMaxMany", { count: maxFiles })
        : t("fileErrorMaxOne");
    }
    for (const file of files) {
      if (!matchesAccept(file, accept)) {
        return t("fileErrorType", {
          name: file.name,
          types: humanizeAccept(accept ?? "") || accept,
          interpolation: NO_INTERPOLATION_ESCAPE,
        });
      }
    }
    if (maxSize) {
      const cap = maxSize * 1024 * 1024;
      for (const file of files) {
        if (file.size > cap) {
          return t("fileErrorTooLarge", {
            name: file.name,
            size: maxSize,
            interpolation: NO_INTERPOLATION_ESCAPE,
          });
        }
      }
    }
    return "";
  };

  useEffect(() => {
    if (!isUploadMode) return;
    onUploadedChange(
      items
        .filter((it) => it.status === "done" && it.code)
        .map((it) => it.code as string),
    );
    onUploadingChange(items.some((it) => it.status === "uploading"));
  }, [items, isUploadMode]);

  const describeUploadFailure = (err: unknown, file: File): string => {
    if (
      (err instanceof UnprocessableEntityError ||
        err instanceof ForbiddenError) &&
      err.message
    ) {
      return err.message;
    }
    if (err instanceof NetworkError) return t("fileUploadOffline");
    if (err instanceof BackendError && err.statusCode === 413 && maxSize) {
      return t("fileErrorTooLarge", {
        name: file.name,
        size: maxSize,
        interpolation: NO_INTERPOLATION_ESCAPE,
      });
    }
    return t("fileUploadFailed");
  };

  const startUpload = (item: DisplayFile, file: File) => {
    if (!uploader) return;
    const controller = new AbortController();
    controllers.current[item.id] = controller;

    milestones.current[item.id] = 0;

    uploader(
      file,
      (percent) => {
        const reached = milestones.current[item.id];
        if (reached === undefined) return;
        const step = Math.floor(percent / PROGRESS_STEP_PERCENT);
        if (percent < 100 && step > reached) {
          milestones.current[item.id] = step;
          announce(
            t("fileProgress", {
              name: item.name,
              percent: step * PROGRESS_STEP_PERCENT,
              interpolation: NO_INTERPOLATION_ESCAPE,
            }),
          );
        }
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, progress: percent } : it,
          ),
        );
      },
      controller.signal,
    )
      .then((result) => {
        announce(
          t("fileComplete", {
            name: item.name,
            interpolation: NO_INTERPOLATION_ESCAPE,
          }),
        );
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? {
                  ...it,
                  status: "done" as const,
                  progress: 100,
                  code: result.code,
                }
              : it,
          ),
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = describeUploadFailure(err, file);
        announce(
          t("fileFailed", {
            name: item.name,
            reason: message,
            interpolation: NO_INTERPOLATION_ESCAPE,
          }),
          "assertive",
        );
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, status: "error" as const, error: message }
              : it,
          ),
        );
      })
      .finally(() => {
        delete controllers.current[item.id];
        delete milestones.current[item.id];
      });
  };

  const resetNativeInput = () => {
    if (innerRef.current) innerRef.current.value = "";
  };

  const addFiles = (fileList: FileList | null) => {
    if (disabled || !fileList || !fileList.length) return;
    const incoming = Array.from(fileList);
    const validationError = validate(incoming);
    if (validationError) {
      setInternalError(validationError);
      announce(validationError, "assertive");
      resetNativeInput();
      return;
    }
    setInternalError("");
    announce(
      incoming.length === 1
        ? t("fileSelected", {
            name: incoming[0].name,
            interpolation: NO_INTERPOLATION_ESCAPE,
          })
        : t("filesSelected", { count: incoming.length }),
    );
    if (!multiple) {
      pendingFocus.current = { kind: "replace", count: itemCount.current };
    }

    if (isUploadMode) {
      if (!multiple) {
        Object.values(controllers.current).forEach((c) => c.abort());
        controllers.current = {};
      }
      const mapped: DisplayFile[] = incoming.map((file) => ({
        id: nextId(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: isImageFile(file.name, file.type)
          ? URL.createObjectURL(file)
          : null,
        progress: 0,
        status: "uploading",
      }));
      const next = multiple ? [...items, ...mapped] : mapped.slice(0, 1);
      setItems(next);
      mapped.forEach((item, index) => startUpload(item, incoming[index]));
      resetNativeInput();
      return;
    }

    onChange(fileList);
  };

  const removeAt = (id: string) => {
    controllers.current[id]?.abort();

    if (isUploadMode) {
      setItems(items.filter((it) => it.id !== id));
      setInternalError("");
      return;
    }

    const remaining = Array.from(value ?? []).filter(
      (_, index) => `v${index}` !== id,
    );
    if (remaining.length === 0 || typeof DataTransfer === "undefined") {
      onChange(null);
    } else {
      const transfer = new DataTransfer();
      remaining.forEach((file) => transfer.items.add(file));
      onChange(transfer.files);
    }
    resetNativeInput();
    setInternalError("");
  };

  const openPicker = () => {
    if (!disabled && innerRef.current) innerRef.current.click();
  };

  const handleDragOver = (event: DragEvent<HTMLFieldSetElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLFieldSetElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLFieldSetElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    addFiles(event.dataTransfer.files);
  };

  const plainItems: DisplayFile[] = [];
  if (!isUploadMode) {
    if (value && value.length > 0) {
      for (let i = 0; i < value.length; i++) {
        plainItems.push({
          id: `v${i}`,
          name: value[i].name,
          size: value[i].size,
          type: value[i].type,
          url: previewUrls[i] ?? null,
          status: "done",
        });
      }
    } else if (existingFileUrl) {
      plainItems.push({
        id: "existing",
        name: existingFileUrl.split("/").pop() || t("fileCurrent"),
        size: 0,
        type: "",
        url: isImageFile(existingFileUrl) ? existingFileUrl : null,
        status: "done",
        isExisting: true,
      });
    }
  }

  const shownItems = isUploadMode ? items : plainItems;
  const hasItems = shownItems.length > 0;
  const singleFilled = !multiple && hasItems;
  const itemCount = useRef(0);
  itemCount.current = shownItems.length;

  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    const active = document.activeElement;
    if (
      active &&
      active !== document.body &&
      !fieldsetRef.current?.contains(active)
    ) {
      pendingFocus.current = null;
      return;
    }
    let target: HTMLElement | null = null;
    if (pending.kind === "replace") target = replaceRef.current;
    if (pending.kind === "trigger") {
      target =
        triggerRef.current ??
        replaceRef.current ??
        cardRemoveRefs.current[shownItems[0]?.id] ??
        null;
    }
    if (pending.kind === "card") {
      const remaining =
        shownItems[Math.min(pending.index ?? 0, shownItems.length - 1)];
      target = remaining
        ? cardRemoveRefs.current[remaining.id]
        : triggerRef.current;
    }
    const countChanged = shownItems.length !== pending.count;
    const removalApplied =
      !shownItems.some((it) => it.id === pending.removedId) || countChanged;
    const ready =
      pending.removedId !== undefined
        ? removalApplied
        : target !== null || countChanged;
    if (!ready) return;
    pendingFocus.current = null;
    target?.focus();
  });

  const autoHint =
    hint ||
    [
      accept &&
        humanizeAccept(accept) !== "" &&
        t("fileAccepted", {
          types: humanizeAccept(accept),
          interpolation: NO_INTERPOLATION_ESCAPE,
        }),
      maxSize &&
        t(multiple ? "fileMaxSizeEach" : "fileMaxSize", { size: maxSize }),
      maxFiles && multiple && t("fileMaxFiles", { count: maxFiles }),
    ]
      .filter(Boolean)
      .join(" · ");

  const placeholderSplit = (placeholder ?? "").indexOf(" or ");
  const titleHead = placeholder
    ? placeholder.slice(0, placeholderSplit < 0 ? undefined : placeholderSplit)
    : t(multiple ? "fileChooseManyHead" : "fileChooseOneHead");
  const titleTail = placeholder
    ? placeholderSplit < 0
      ? ""
      : placeholder.slice(placeholderSplit + 1)
    : t("fileDragTail");
  const title = placeholder || [titleHead, titleTail].join(" ");

  const triggerText = compact ? t("fileUpload") : title;
  const hintId = `${name}-hint`;
  const errorId = `${name}-error`;
  const hintRendered = !!autoHint && (!compact || !displayError);
  const describedBy =
    [hintRendered && hintId, displayError && errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  const errorLink = displayError
    ? ({ "aria-invalid": true, "aria-describedby": errorId } as const)
    : {};

  const removeItem = (item: DisplayFile) => {
    announce(
      t("fileRemoved", {
        name: item.name,
        interpolation: NO_INTERPOLATION_ESCAPE,
      }),
    );
    pendingFocus.current = multiple
      ? {
          kind: "card",
          removedId: item.id,
          index: shownItems.findIndex((it) => it.id === item.id),
          count: shownItems.length,
        }
      : { kind: "trigger", removedId: item.id, count: shownItems.length };
    if (item.isExisting) {
      onDeleteExisting();
      return;
    }
    removeAt(item.id);
  };

  const renderCard = (item: DisplayFile) => {
    const uploading = item.status === "uploading";
    const done = item.status === "done" && !item.error;
    const image = !!item.url && isImageFile(item.name, item.type);

    return (
      <div
        key={item.id}
        className="flex items-center gap-3 rounded-lg border border-divider px-2.5 py-2 bg-page"
      >
        {image ? (
          <img
            src={item.url as string}
            alt={item.name}
            className="w-10 h-10 flex-shrink-0 rounded object-cover border border-divider bg-surface"
          />
        ) : (
          <span className="relative w-10 h-10 flex-shrink-0 grid place-items-center rounded bg-tint text-primary">
            {isImageFile(item.name, item.type) ? (
              <ImageIcon size={18} />
            ) : (
              <FileIcon size={18} />
            )}
            <span className="absolute bottom-0.5 inset-x-0 text-center text-[0.5rem] font-bold tracking-wide text-primary">
              {extensionOf(item.name)}
            </span>
          </span>
        )}

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="text-sm font-medium text-default truncate">
            {item.name}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted">
            {item.error ? (
              <span className="text-danger">{item.error}</span>
            ) : (
              <>
                {item.size ? formatSize(item.size) : null}
                {uploading && (
                  <span>
                    · {t("fileUploading", { percent: item.progress ?? 0 })}
                  </span>
                )}
              </>
            )}
          </span>
          {uploading && (
            <div className="h-1 rounded-full bg-surface overflow-hidden mt-0.5">
              <div
                className="h-full bg-primary rounded-full transition-[width] duration-200"
                style={{ width: `${item.progress ?? 0}%` }}
              />
            </div>
          )}
        </div>

        {done && (
          <span className="flex text-success flex-shrink-0">
            <CircleCheck size={18} />
          </span>
        )}
        {!disabled && (
          <button
            type="button"
            className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full text-muted hover:bg-surface hover:text-danger transition-colors"
            {...errorLink}
            ref={(element) => {
              cardRemoveRefs.current[item.id] = element;
            }}
            aria-label={t("fileRemove", {
              name: item.name,
              interpolation: NO_INTERPOLATION_ESCAPE,
            })}
            onClick={(e) => {
              e.stopPropagation();
              removeItem(item);
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <fieldset
      ref={fieldsetRef}
      className="flex flex-col w-full"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {label && (
        <Label
          name={name}
          label={label}
          info={info}
          error={displayError}
          required={required}
          displayInfo={displayInfo}
          infoId={`${name}-info`}
          setDisplayInfo={setDisplayInfo}
        />
      )}

      <input
        ref={innerRef}
        id={name}
        name={name}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => addFiles(e.target.files)}
      />

      {singleFilled ? (
        <div className="mt-1">
          {renderCard(shownItems[0])}
          {!disabled && (
            <button
              ref={replaceRef}
              type="button"
              {...errorLink}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary"
              onClick={openPicker}
            >
              <Upload size={13} /> {t("fileReplace")}
            </button>
          )}
        </div>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          aria-label={
            label
              ? t("fileTriggerName", {
                  label,
                  prompt: triggerText,
                  interpolation: NO_INTERPOLATION_ESCAPE,
                })
              : triggerText
          }
          aria-invalid={displayError ? true : undefined}
          aria-describedby={describedBy}
          className={classNames(
            "mt-1 w-full appearance-none flex items-center justify-center gap-1 rounded-lg border-2 border-dashed text-center cursor-pointer transition-colors",
            compact ? "flex-row px-4 py-3" : "flex-col px-5 py-6",
            {
              "border-default bg-page hover:bg-page hover:border-default":
                !isDragOver && !displayError && !disabled,
              "border-primary bg-tint": isDragOver && !displayError,
              "border-danger bg-danger-subtle": !!displayError,
              "cursor-not-allowed opacity-60": disabled,
              "bg-surface": disabled && !displayError,
            },
          )}
          onClick={openPicker}
        >
          <span
            className={classNames(
              "grid place-items-center rounded-full",
              compact ? "w-8 h-8" : "w-11 h-11 mb-1.5",
              displayError
                ? "bg-danger-subtle text-danger"
                : "bg-surface text-primary",
            )}
          >
            <Upload size={compact ? 16 : 20} />
          </span>
          <span
            className={classNames(
              "text-sm font-medium",
              displayError ? "text-danger" : "text-default",
            )}
          >
            <span className="text-primary underline underline-offset-2">
              {compact ? t("fileUpload") : titleHead}
            </span>
            {!compact && titleTail ? ` ${titleTail}` : ""}
          </span>
          {!compact && !(accept && !multiple) && (
            <span className="text-xs text-muted">
              {multiple ? t("fileAddMany") : t("fileAnyOne")}
            </span>
          )}
          {!compact && autoHint && (
            <span
              id={hintId}
              className="mt-2 block text-[0.71875rem] leading-relaxed text-muted"
            >
              {autoHint}
            </span>
          )}
        </button>
      )}

      {multiple && hasItems && (
        <div className="flex flex-col gap-2 mt-2.5">
          {shownItems.map((item) => renderCard(item))}
        </div>
      )}

      {displayError ? (
        <div
          id={errorId}
          className="flex items-center gap-1.5 mt-1.5 text-xs text-danger"
        >
          <span className="flex flex-shrink-0">
            <TriangleAlert size={14} />
          </span>
          {displayError}
        </div>
      ) : (
        compact &&
        autoHint && (
          <div id={hintId} className="mt-1.5 text-xs text-muted">
            {autoHint}
          </div>
        )
      )}

      <Info id={`${name}-info`} info={info} displayInfo={displayInfo} />
    </fieldset>
  );
}

export default FileInput;
